import { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  BalancedFeedbackData,
  CoachingHistoryItem,
  NewActionDraft,
  SignatureData,
  FollowUpResult,
  WeeklyMetricRow,
  WeeklyMetricCell,
} from '../../common/types/beginActivity.common.types';
import { fromCoachingResultEnum, toSignaturePayload, mapResumedSignatures, withCreatedBy, buildNewActionPlanPayload, buildCoachingResultPayload, validateNewActionPlan } from '../../common/activityPayload';
import { saveActivityDraft, completeActivity } from '../../../peopleHubApi';
import { mapWeeklyMetricRows, mapPeriodMetricRows, mapCoachingHistoryItems } from '../../../EmployeeProfile/mapEmployeeProfileResponse';
import type { ActivitySession } from '../../PeopleActivityScreen';

// Re-export so callers that import from this hook still work
export type { ActivitySession };


function allResultsRecorded(openItems: CoachingHistoryItem[]): boolean {
  return openItems.length === 0 || openItems.every((i) => !!i.followUpResult);
}

function allFeedbackComplete(openItems: CoachingHistoryItem[]): boolean {
  return openItems.every((i) => {
    const needsFeedback =
      i.followUpResult === 'Shows Improvement' ||
      i.followUpResult === 'No Improvement Shown';
    return !needsFeedback || i.followUpFeedback.trim().length >= 10;
  });
}

function allItemsComplete(openItems: CoachingHistoryItem[]): boolean {
  return (
    openItems.length === 0 ||
    openItems.every((i) => i.followUpResult === 'Complete')
  );
}

// ─── Mock metric rows (per the HTML seed data structure) ─────────────────────
function buildMockMetricRows(): WeeklyMetricRow[] {
  const makeCell = (
    wk: number,
    actual: number,
    target: number,
  ): WeeklyMetricCell => ({
    weekLabel: `W${wk}`,
    actual,
    target,
    state: actual >= target ? 'met' : 'miss',
  });

  return [
    {
      metricName: 'Cases Picked',
      unit: 'cases',
      rollingAverage: 1189,
      weeksMet: 9,
      weeksTotal: 12,
      cells: [
        makeCell(13, 1210, 1150), makeCell(14, 1089, 1150),
        makeCell(15, 1132, 1150), makeCell(16, 1098, 1150),
        makeCell(17, 1176, 1150), makeCell(18, 1205, 1150),
        makeCell(19, 1158, 1150), makeCell(20, 1192, 1150),
        makeCell(21, 1248, 1150), makeCell(22, 1276, 1150),
        makeCell(23, 1214, 1150), makeCell(24, 1188, 1150),
      ],
    },
    {
      metricName: 'DPS',
      unit: '%',
      rollingAverage: 0.971,
      weeksMet: 8,
      weeksTotal: 12,
      cells: [
        makeCell(13, 0.921, 1.000), makeCell(14, 0.953, 1.000),
        makeCell(15, 0.976, 1.000), makeCell(16, 0.948, 1.000),
        makeCell(17, 0.987, 1.000), makeCell(18, 0.992, 1.000),
        makeCell(19, 0.963, 1.000), makeCell(20, 0.971, 1.000),
        makeCell(21, 1.002, 1.000), makeCell(22, 1.014, 1.000),
        makeCell(23, 0.986, 1.000), makeCell(24, 1.031, 1.000),
      ],
    },
    {
      metricName: 'Gap Time',
      unit: 'sec',
      rollingAverage: 5.1,
      weeksMet: 6,
      weeksTotal: 12,
      cells: [
        makeCell(13, 5.8, 5.0), makeCell(14, 5.5, 5.0),
        makeCell(15, 5.3, 5.0), makeCell(16, 5.4, 5.0),
        makeCell(17, 5.1, 5.0), makeCell(18, 4.9, 5.0),
        makeCell(19, 5.2, 5.0), makeCell(20, 4.8, 5.0),
        makeCell(21, 4.7, 5.0), makeCell(22, 4.9, 5.0),
        makeCell(23, 4.8, 5.0), makeCell(24, 4.6, 5.0),
      ],
    },
    {
      metricName: 'BPA',
      unit: 'ratio',
      rollingAverage: 1.22,
      weeksMet: 8,
      weeksTotal: 12,
      cells: [
        makeCell(13, 1.12, 1.25), makeCell(14, 1.15, 1.25),
        makeCell(15, 1.18, 1.25), makeCell(16, 1.14, 1.25),
        makeCell(17, 1.22, 1.25), makeCell(18, 1.25, 1.25),
        makeCell(19, 1.20, 1.25), makeCell(20, 1.23, 1.25),
        makeCell(21, 1.31, 1.25), makeCell(22, 1.29, 1.25),
        makeCell(23, 1.24, 1.25), makeCell(24, 1.32, 1.25),
      ],
    },
  ];
}

// ─── Mock coaching data ───────────────────────────────────────────────────────
function buildMockOpenItems(employeeId: string): CoachingHistoryItem[] {
  return [
    {
      id: `open-${employeeId}-1`,
      employeeId,
      source: 'Observation',
      activityId: 'act-001',
      date: '2026-06-09',
      submittedBy: 'S. Okafor',
      category: 'Metric',
      focus: 'Gap Time',
      plan: 'Stage the replenishment pallets before the wave to hold gap time under 5 minutes.',
      status: 'Pending',
      followUpResult: '',
      followUpFeedback: '',
      reviewHistory: [],
    },
  ];
}

function buildMockHistoryItems(employeeId: string): CoachingHistoryItem[] {
  return [
    {
      id: `hist-${employeeId}-1`,
      employeeId,
      source: 'Pulse Check',
      activityId: 'act-090',
      date: '2026-07-24',
      submittedBy: 'S. Okafor',
      category: 'Metric',
      focus: 'Gap Time',
      plan: 'Sequence the replenishment pallets ahead of the wave for the full follow up period.',
      status: 'Completed',
      followUpResult: 'Complete',
      followUpFeedback: '',
      completedOn: '2026-07-24',
      reviewHistory: [],
    },
    {
      id: `hist-${employeeId}-2`,
      employeeId,
      source: 'Observation',
      activityId: 'act-080',
      date: '2026-06-02',
      submittedBy: 'S. Okafor',
      category: 'Process',
      focus: 'Geo Loading',
      plan: 'Load to the geo sequence on every outbound trailer this period.',
      status: 'Completed',
      followUpResult: 'Complete',
      followUpFeedback: '',
      completedOn: '2026-06-30',
      reviewHistory: [],
    },
  ];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function usePulseCheck(session: ActivitySession) {
  const userInfo = useSelector((state: any) => state.appData?.userInfo);
  const selectedLocation = useSelector((state: any) => state.appData?.selectedLocation);
  const fieldUsers = useSelector((state: any) => state.appData?.FieldUsers ?? []);
  const coeUsers = useSelector((state: any) => state.appData?.COEUsers ?? []);
  const employeesRaw = useSelector((state: any) => state.appData?.employees);
  const employeeRoster = useMemo(
    () => (Array.isArray(employeesRaw) ? employeesRaw : employeesRaw?.data ?? []),
    [employeesRaw],
  );
  const profileData = useSelector((state: any) => state.peopleHub?.employeeProfile?.data);

  const metricRows = useMemo<WeeklyMetricRow[]>(() => {
    const mapped = mapWeeklyMetricRows(profileData?.metrics12Week);
    return mapped.length > 0 ? mapped : buildMockMetricRows();
  }, [profileData]);

  // Period rows — shown when the metrics card is toggled to "Period"
  const periodMetricRows = useMemo<WeeklyMetricRow[]>(() => {
    const mapped = mapPeriodMetricRows(profileData?.metrics12Period);
    return mapped.length > 0 ? mapped : metricRows;
  }, [profileData, metricRows]);

  const [feedback, setFeedback] = useState<BalancedFeedbackData>(() => ({
    good: session.resumedFeedback?.recognize ?? '',
    improve: session.resumedFeedback?.coach ?? '',
    keep: session.resumedFeedback?.sustain ?? '',
    support: '',
  }));

  const [openItems, setOpenItems] = useState<CoachingHistoryItem[]>(() => {
    const items = buildMockOpenItems(session.employeeId);
    const rawResult = typeof session.resumedCoachingResult === 'object' && session.resumedCoachingResult !== null
      ? (session.resumedCoachingResult as any).followup_result ?? (session.resumedCoachingResult as any).status
      : session.resumedCoachingResult;
    const resumedResult = fromCoachingResultEnum(rawResult);
    const resumedSupervisorFeedback = typeof session.resumedCoachingResult === 'object' && session.resumedCoachingResult !== null
      ? (session.resumedCoachingResult as any).supervisorFeedback ?? session.resumedSupervisorFeedback
      : session.resumedSupervisorFeedback;
    if (items[0] && resumedResult) {
      items[0] = {
        ...items[0],
        followUpResult: resumedResult,
        followUpFeedback: resumedSupervisorFeedback ?? '',
      };
    }
    return items;
  });

  const [historyItems, setHistoryItems] = useState<CoachingHistoryItem[]>(
    () => buildMockHistoryItems(session.employeeId),
  );

  // Swaps the mock coaching rows for the employee's real coachingItems once the profile
  // fetch (dispatched alongside the resume call) lands. An empty array is a valid "no open
  // items" result and must still replace the mock rows, so only bail when the profile
  // hasn't loaded yet (coachingItems is undefined).
  useEffect(() => {
    const items = profileData?.coachingItems;
    if (!Array.isArray(items)) return;
    const { openItems: apiOpen, historyItems: apiHistory } = mapCoachingHistoryItems(items, session.employeeId);
    const rawResult = typeof session.resumedCoachingResult === 'object' && session.resumedCoachingResult !== null
      ? (session.resumedCoachingResult as any).followup_result ?? (session.resumedCoachingResult as any).status
      : session.resumedCoachingResult;
    const resumedResult = fromCoachingResultEnum(rawResult);
    const resumedSupervisorFeedback = typeof session.resumedCoachingResult === 'object' && session.resumedCoachingResult !== null
      ? (session.resumedCoachingResult as any).supervisorFeedback ?? session.resumedSupervisorFeedback
      : session.resumedSupervisorFeedback;
    if (apiOpen[0] && resumedResult) {
      apiOpen[0] = {
        ...apiOpen[0],
        followUpResult: resumedResult,
        followUpFeedback: resumedSupervisorFeedback ?? '',
      };
    }
    setOpenItems(apiOpen);
    setHistoryItems(apiHistory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData]);

  const [actionDraft, setActionDraft] = useState<NewActionDraft>({
    type: '', focus: '', otherFocus: '', opportunity: '', plan: '',
  });

  const [sigData, setSigData] = useState<SignatureData>(() =>
    mapResumedSignatures(
      session.resumedSignatures ?? session.signatures,
      session.employeeName,
      session.managerName,
    ),
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSignatureUploading, setIsSignatureUploading] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // ── Follow-up handlers ──────────────────────────────────────────────
  const handleFollowUpResult = (id: string, result: FollowUpResult) => {
    setOpenItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, followUpResult: result, followUpFeedback: '' } : item,
      ),
    );
  };

  const handleFollowUpFeedback = (id: string, text: string) => {
    setOpenItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, followUpFeedback: text } : item,
      ),
    );
  };

  // ── Validation ──────────────────────────────────────────────────────
  const newActionPlanContext = useMemo(() => ({
    employeeId: session.employeeId,
    employeeName: session.employeeName,
    activityId: session.activityId,
    userInfo,
    // profileData.employee.manager (the live /people/employees/{id} record) is the
    // authoritative source — session.managerName can be stale/wrong on resume.
    ownerName: profileData?.employee?.manager ?? session.managerName,
    ownerEmail: profileData?.employee?.managerEmail,
    ownerGpid: profileData?.employee?.managerGpid,
    locationName: selectedLocation?.name ?? selectedLocation?.displayText,
    locationId: selectedLocation?.id,
    support: feedback.support,
    directoryUsers: [...fieldUsers, ...coeUsers],
    employeeRoster,
  }), [session, userInfo, selectedLocation, feedback.support, fieldUsers, coeUsers, employeeRoster, profileData]);

  const validationError = useMemo<string | null>(() => {
    if (!allResultsRecorded(openItems))
      return 'Record a follow up result for every open coaching item before completing the Pulse Check.';
    if (!allFeedbackComplete(openItems))
      return 'Supervisor Feedback must contain at least 10 characters when Shows Improvement or No Improvement Shown is selected.';
    if (actionDraft.type && !allItemsComplete(openItems))
      return 'A new action item can only be created when every open item is marked Complete.';
    const newActionErr = validateNewActionPlan(actionDraft, newActionPlanContext);
    if (newActionErr) return newActionErr;
    const hasEmployeeSig = !!(sigData.employeeSignature?.imageData || sigData.employeeSignature?.imageUrl);
    const hasManagerSig = !!(sigData.managerSignature?.imageData || sigData.managerSignature?.imageUrl);
    if (!hasEmployeeSig || !hasManagerSig)
      return 'Employee and manager signatures are required before completing the Pulse Check.';
    if (isSignatureUploading)
      return 'Waiting for signatures to finish uploading…';
    return null;
  }, [openItems, actionDraft, sigData, isSignatureUploading, newActionPlanContext]);

  const canComplete = validationError === null;

  // ── Gate for NewAction (all open items must be Complete) ────────────
  const newActionGated = !allItemsComplete(openItems);

  const gateMessage = newActionGated
    ? 'Select Shows Improvement, No Improvement Shown, or Complete on every open item before adding a new action.'
    : undefined;

  // ── Submit ──────────────────────────────────────────────────────────
  // The top-level coachingResult/supervisorFeedback mirror the first open item's
  // follow-up entry — PULSE_CHECK save/complete carries a single coaching outcome.
  // newAction (when the user filled out the New Action form) is sent separately and
  // creates its own coaching item server-side, independent of coachingResult.
  const buildPulseCheckPayload = () => {
    const primaryOpenItem = openItems[0];
    return {
      activityType: 'PULSE_CHECK',
      balance_feedback: {
        recognize: feedback.good,
        coach: feedback.improve,
        sustain: feedback.keep,
      },
      coachingResult: buildCoachingResultPayload(primaryOpenItem),
      // Full Action Plan creation fields (title/source/type/priority/owner/assignee/dates) —
      // undefined when the New Action form was left blank, or when assignee/owner identity
      // fields can't be resolved (buildNewActionPlanPayload guards via validateNewActionAssignment).
      newAction: buildNewActionPlanPayload(actionDraft, newActionPlanContext),
    };
  };

  const handleSaveDraft = async (onDone?: () => void) => {
    if (session.activityId == null) return;
    // Never call the API while the New Action form is incomplete or unresolved.
    if (validateNewActionPlan(actionDraft, newActionPlanContext)) return;
    setIsSavingDraft(true);
    const { employeeSignature, managerSignature } = toSignaturePayload(sigData);
    try {
      await saveActivityDraft(session.activityId, withCreatedBy({
        ...buildPulseCheckPayload(),
        employeeSignature,
        managerSignature,
      }, userInfo));
      onDone?.();
    } catch (err) {
      console.warn('Failed to save pulse check draft:', err);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleComplete = async (onDone: () => void) => {
    if (!canComplete || session.activityId == null) return;
    setIsSubmitting(true);
    try {
      const { employeeSignature, managerSignature } = toSignaturePayload(sigData);
      await completeActivity(session.activityId, withCreatedBy({
        ...buildPulseCheckPayload(),
        employeeSignature,
        managerSignature,
      }, userInfo));
      onDone();
    } catch (err) {
      console.warn('Failed to complete pulse check:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    // data
    metricRows,
    periodMetricRows,
    feedback,
    openItems,
    historyItems,
    actionDraft,
    sigData,
    // setters
    setFeedback,
    setActionDraft,
    setSigData,
    isSignatureUploading,
    setIsSignatureUploading,
    handleFollowUpResult,
    handleFollowUpFeedback,
    // status
    validationError,
    canComplete,
    isSubmitting,
    isSavingDraft,
    newActionGated,
    gateMessage,
    // actions
    handleSaveDraft,
    handleComplete,
  };
}
